import React from "react";
import { Link } from "react-router-dom";

import { useFetchBiProductsQuery } from "../../../api/analyticsApi";
import { DataState, DataTable, Panel, ProductCell } from "../AnalyticsUi";
import { costBasisLabel, fill, formatDateTime, formatMoney, formatNumber, formatPercent } from "../analyticsModel";

export const CostCoverage = ({ cost, tt, lang }) => {
  if (!cost) return null;
  return (
    <Panel title={tt("costCoverageTitle")} hint={tt("costCoverageHint")}>
      <div className="an-coverage">
        <div><span>{tt("costBasis")}</span><strong>{costBasisLabel(tt, cost) || tt("unavailable")} · {tt(`costStatus_${cost.status}`)}</strong></div>
        <div><span>{tt("salesCostCoverage")}</span><strong>{formatPercent(cost.coverage, lang)}</strong></div>
        <div><span>{tt("recordedSalesCoverage")}</span><strong>{formatPercent(cost.recordedCoverage, lang)}</strong></div>
        <div><span>{tt("returnCostCoverage")}</span><strong>{formatPercent(cost.returnCoverage, lang)}</strong></div>
        <div><span>{tt("recordedReturnCoverage")}</span><strong>{formatPercent(cost.recordedReturnCoverage, lang)}</strong></div>
      </div>
      {cost.status === "partial" || cost.status === "unavailable" ? <p className="an-muted">{fill(tt("uncoveredAmounts"), { sales: formatMoney(cost.uncoveredRevenue, lang), returns: formatMoney(cost.uncoveredRefunds, lang) })}</p> : null}
    </Panel>
  );
};

export const DeliveryReconciliation = ({ data, tt, lang }) => {
  if (!data) return null;
  return (
    <Panel id="an-reconciliation" title={tt("reconciliationTitle")} hint={tt("reconciliationHint")}>
      {data.total > 0 ? (
        <>
          <p className="an-reconciliation-summary" role="status">{fill(tt("reconciliationSummary"), { count: formatNumber(data.total, lang), value: formatMoney(data.merchandiseValue, lang) })}</p>
          <DataTable
            caption={tt("reconciliationTitle")}
            columns={[
              { key: "order", label: tt("orderNumber"), render: row => <Link to="/admin/orders">{row.orderNumber}</Link> },
              { key: "date", label: tt("deliveredAt"), render: row => formatDateTime(row.deliveredAt, lang) },
              { key: "value", label: tt("merchandiseValue"), align: "end", render: row => formatMoney(row.merchandiseValue, lang) },
            ]}
            rows={data.items} rowKey={row => row.orderId}
          />
          <p className="an-muted">{fill(tt("reconciliationLimit"), { shown: data.items.length, total: data.total })}</p>
        </>
      ) : <p className="an-muted">{tt("reconciliationClear")}</p>}
    </Panel>
  );
};

export const BestSellingProducts = ({ range, tt, lang }) => {
  const result = useFetchBiProductsQuery(
    { dateFrom: range.dateFrom, dateTo: range.dateTo, sort: "netUnits", direction: "desc", performance: "selling", pageSize: 5 },
    { pollingInterval: 60_000, skipPollingIfUnfocused: true, refetchOnMountOrArgChange: true },
  );
  const query = { ...result, data: result.currentData };
  const rows = query.data?.items || [];
  return (
    <Panel title={tt("bestSellingTitle")} hint={tt("bestSellingHint")}>
      <DataState query={query} tt={tt} isEmpty={query.data && !rows.length}>
        <DataTable
          caption={tt("bestSellingTitle")}
          columns={[
            { key: "product", label: tt("col_product"), render: row => <ProductCell tt={tt} productId={row.productId} imageUrl={row.imageUrl} title={row.title} sub={row.categoryName || tt("uncategorised")} /> },
            { key: "units", label: tt("col_netUnits"), align: "end", render: row => formatNumber(row.netUnits, lang) },
            { key: "revenue", label: tt("col_netRevenue"), align: "end", render: row => formatMoney(row.netRevenue, lang) },
          ]}
          rows={rows} rowKey={row => row.productId}
        />
      </DataState>
    </Panel>
  );
};
